// LiveStreamCard.jsx
import React, { createElement } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';

import LiveStreamBadge from '../container/LiveStreamBadge.js';

import WithIntersectionObserver from '../component/WithIntersectionObserver.jsx';
import HydrationBoundary from '../component/HydrationBoundary.jsx';
import StatefulImage from '../component/StatefulImage.jsx';
import LazyImage from '../component/LazyImage.jsx';
import Flip from '../component/Flip.jsx';
import TrailerBlocker from '../container/TrailerBlocker.js';

import {
  textColor,
  breakpoint,
  loadingIconOpacity,
} from '../style/variables.js';
import media from '../style/media.js';

import { LIVESTREAM_TYPE_STATUS_BADGE } from '../resource/userStatusIndicatorConstants.js';
import { getLiveSource } from '../resource/playerUtils.js';
import {
  LIVESTREAM_PATH,
  PRESET_PREVIEW,
  privateShowStatus as privateShowStatusConstant,
} from '../resource/liveStreamConstants.js';
import trackMixpanel, {
  EventTypes,
  getViewId,
  ButtonId,
} from '../resource/mixpanel.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import { PUBLIC } from '../resource/resourceUrlTypeConstants.js';
import { getUserPicture, MediaAssetFormat } from '../resource/getMediaAsset.js';
import getPublicSrcSet from '../resource/getPublicSrcSet.js';
import TextEllipsis from '../style/TextEllipsis.js';

import ViewersIconResource from '../../img/ic_streaming_view_xs.svg';
import LoadingImageResource from '../../img/img_loading_white_20.svg';
import CoverPlaceholderImageResource from '../../img/placeholder-bg-logowall.svg';
import IconDeviceSource from '../../img/ic_lovense_badge.svg';
import { withTranslation } from 'react-i18next';

const isServer = typeof window === 'undefined';
const DELAY_LOADING_MSEC = 1000;

const videoPlayerOption = {
  loop: false,
};
const PLAYER_STATUS = {
  idle: 'idle',
  playing: 'playing',
  loading: 'loading',
  error: 'error',
};

export class LiveStreamCard extends React.PureComponent {
  timeout = null;
  idleCallback = null;
  state = {
    isHovered: false,
    playerStatus: this.props.shouldAutoplay
      ? PLAYER_STATUS.loading
      : PLAYER_STATUS.idle,
    videoPlayerEl: null,
  };

  handleOnMouseEnter = () => {
    this.setState(prevState => ({
      isHovered: true,
      playerStatus:
        prevState.playerStatus === PLAYER_STATUS.playing
          ? PLAYER_STATUS.playing
          : PLAYER_STATUS.loading,
    }));
  };

  handleOnMouseLeave = () => {
    this.setState({
      isHovered: false,
      playerStatus: PLAYER_STATUS.idle,
    });
  };

  handlePlayerSetup = () => {
    this.setState({ playerStatus: PLAYER_STATUS.loading });
  };

  handlePlayerError = (error, player) => {
    player.playlist.next();
    this.setState({ playerStatus: PLAYER_STATUS.error });
  };

  handlePlayerPlaying = () => {
    this.setState({ playerStatus: PLAYER_STATUS.playing });
  };

  renderPlayer = () => {
    const { streamId, shouldAutoplay } = this.props;
    const { playerStatus, isHovered, videoPlayerEl } = this.state;
    if (!streamId || (!isHovered && !shouldAutoplay) || !videoPlayerEl)
      return null;

    const VideoPlayer = createElement(videoPlayerEl, {
      onSetup: this.handlePlayerSetup,
      source: getLiveSource({ streamId, preset: PRESET_PREVIEW }),
      option: videoPlayerOption,
      shouldCombineTrailer: true,
      onError: this.handlePlayerError,
      onPlaying: this.handlePlayerPlaying,
    });
    return (
      <PlayerWrapper shouldShowPlayer={playerStatus === PLAYER_STATUS.playing}>
        {[PLAYER_STATUS.loading, PLAYER_STATUS.playing].includes(
          playerStatus
        ) && VideoPlayer}
        <TrailerBlocker />
      </PlayerWrapper>
    );
  };

  renderHydrateCover = () => {
    const { streamId, userId, username, meId } = this.props;
    const errorComponent = (
      <Picture width="375" height="667" data-key="error">
        <img
          src={CoverPlaceholderImageResource}
          width="375"
          height="667"
          alt="default cover"
        />
      </Picture>
    );
    if (!streamId) {
      return <StatefulImageWrapper>{errorComponent}</StatefulImageWrapper>;
    }

    const userPictureSource = getUserPicture({
      userId,
      meId,
      size: 512,
      format: MediaAssetFormat.JPG,
      isLowCache: true,
    }).href;
    return (
      <WithIntersectionObserver threshold={0.2}>
        {({ isIntersecting }) => (
          <HydrationBoundary
            shouldHydrate={isIntersecting}
            wrapper={<StatefulImageWrapper />}
          >
            <StatefulImage>
              <LoadingWrapper data-key="loading">
                <LoadingIcon src={LoadingImageResource} />
              </LoadingWrapper>
              {errorComponent}
              <Picture data-key="target">
                {[MediaAssetFormat.AVIF, MediaAssetFormat.WEBP].map(format => (
                  <source
                    key={format}
                    type={`image/${format}`}
                    {...getPublicSrcSet({
                      href: userPictureSource,
                      size: 512,
                      format,
                    })}
                  />
                ))}
                <img
                  {...getPublicSrcSet({
                    href: userPictureSource,
                    size: 512,
                    format: MediaAssetFormat.JPG,
                  })}
                  alt={`${username}'s cover`}
                />
              </Picture>
            </StatefulImage>
          </HydrationBoundary>
        )}
      </WithIntersectionObserver>
    );
  };

  renderCover = () => {
    const { userId, streamId, username, meId } = this.props;
    if (!streamId) {
      return (
        <StatefulImageWrapper>
          <LazyImage
            src={CoverPlaceholderImageResource}
            alt="default cover"
            threshold={0.01}
          />
        </StatefulImageWrapper>
      );
    }
    const userPictureSource = getUserPicture({
      userId,
      meId,
      size: 512,
      format: MediaAssetFormat.JPG,
      isLowCache: true,
    }).href;
    return (
      <StatefulImageWrapper>
        <StatefulImage>
          <LoadingWrapper data-key="loading">
            <LoadingIcon src={LoadingImageResource} />
          </LoadingWrapper>
          <LazyImage
            data-key="error"
            src={CoverPlaceholderImageResource}
            alt="default cover"
            threshold={0.01}
          />
          <LazyImage
            data-key="target"
            {...getPublicSrcSet({
              href: userPictureSource,
              size: 512,
              format: MediaAssetFormat.JPG,
            })}
            alt={`${username}'s cover`}
            threshold={0.01}
          >
            {[MediaAssetFormat.AVIF, MediaAssetFormat.WEBP].map(format => (
              <source
                key={format}
                type={`image/${format}`}
                {...getPublicSrcSet({
                  href: userPictureSource,
                  size: 512,
                  format,
                })}
              />
            ))}
          </LazyImage>
        </StatefulImage>
      </StatefulImageWrapper>
    );
  };

  renderLocation = () => {
    const { t, locationKey, i18n, isJumbo, isLocationKeyTranslationFetched } =
      this.props;
    if (!locationKey) return null;

    const locationPlaceholder = locationKey.replace(/location_/i, '');
    return (
      <LocationWrapper isJumbo={isJumbo}>
        <LocationText isJumbo={isJumbo}>
          {i18n.exists(locationKey) && isLocationKeyTranslationFetched
            ? t(locationKey)
            : locationPlaceholder}
        </LocationText>
      </LocationWrapper>
    );
  };

  fetchData = () => {
    const {
      i18n,
      username,
      locationKey,
      languageBadges,
      streamId,
      isIntersecting,
      fetchUser,
      fetchTranslationByKey,
    } = this.props;

    if ((!username || !languageBadges.length) && isIntersecting && streamId) {
      fetchUser();
    }

    if (locationKey && !i18n.exists(locationKey)) {
      fetchTranslationByKey({ key: locationKey });
    }
  };

  subscribeChannel = () => {
    const { isIntersecting, subscribeOnlineStatus } = this.props;

    if (isIntersecting) {
      return subscribeOnlineStatus();
    }
  };

  loadVideoPlayer = () => {
    import('./VideoPlayer.jsx')
      .then(module => module.default)
      .then(VideoPlayer => {
        this.setState({
          videoPlayerEl: VideoPlayer,
        });
      });
  };

  loadVideo = () => {
    const { videoPlayerEl, isHovered } = this.state;
    const { shouldAutoplay } = this.props;

    this.clearSideEffect();

    if (videoPlayerEl || (!shouldAutoplay && !isHovered)) {
      return;
    }

    const delayLoadVideoPlayer = () => {
      this.timeout = setTimeout(() => {
        this.loadVideoPlayer();
      }, DELAY_LOADING_MSEC);
    };

    if (isHovered) {
      this.loadVideoPlayer();
    } else {
      if ('requestIdleCallback' in window) {
        this.idleCallback = window.requestIdleCallback(delayLoadVideoPlayer);
      } else {
        delayLoadVideoPlayer();
      }
    }
  };

  clearSideEffect = () => {
    if ('cancelIdleCallback' in window) {
      window.cancelIdleCallback(this.idleCallback);
    }
    clearTimeout(this.timeout);
  };

  handleClickCard = () => {
    const { userId, status, privateShowStatus, formattedViewers } = this.props;
    if (!userId) {
      return;
    }

    const goalStatus = [
      privateShowStatusConstant.PREPARING,
      privateShowStatusConstant.PERFORMING,
    ].includes(privateShowStatus)
      ? 'show'
      : [
          privateShowStatusConstant.FUNDING,
          privateShowStatusConstant.FUNDING_FINAL_CALL,
        ].includes(privateShowStatus)
      ? 'show-funding'
      : '';
    const eventPayload = {
      viewId: getViewId({
        pathname: window.location.pathname,
      }),
      buttonId: ButtonId.All.ButtonLiveStreamCard,
      extraProps: {
        'user.id': userId,
        preset: status,
        ...(goalStatus && {
          'goal.status': goalStatus,
        }),
        'stream.viewers': formattedViewers,
      },
    };
    if (location.pathname === '/') {
      eventPayload.extraProps['discover.category'] = 'livestream';
    }
    trackMixpanel({
      type: EventTypes.USER_CLICKED,
      payload: eventPayload,
    });
  };

  handleClickUsername = () => {
    const { userId } = this.props;
    if (!userId) {
      return;
    }
    const eventPayload = {
      viewId: getViewId({
        pathname: window.location.pathname,
      }),
      buttonId: ButtonId.All.ButtonCardAvatar,
      extraProps: {},
    };
    if (location.pathname === '/') {
      eventPayload.extraProps['discover.category'] = 'livestream';
    }
    trackMixpanel({
      type: EventTypes.USER_CLICKED,
      payload: eventPayload,
    });
  };

  componentDidMount() {
    this.fetchData();
    this.subscribeChannel();
    this.loadVideo();
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      streamId: prevStreamId,
      isAuthed: prevIsAuthed,
      isIntersecting: prevIsIntersecting,
      shouldAutoplay: prevShouldAutoplay,
      locationKey: prevLocationKey,
    } = prevProps;
    const { streamId, isAuthed, isIntersecting, shouldAutoplay, locationKey } =
      this.props;
    const { isHovered } = this.state;
    const { isHovered: prevIsHovered } = prevState;

    if (
      prevIsAuthed !== isAuthed ||
      prevIsIntersecting !== isIntersecting ||
      streamId !== prevStreamId ||
      locationKey !== prevLocationKey
    ) {
      this.fetchData();
      this.subscribeChannel();
    }

    if (prevShouldAutoplay !== shouldAutoplay) {
      this.loadVideo();
      this.setState({
        isHovered: false,
        playerStatus: shouldAutoplay
          ? PLAYER_STATUS.loading
          : PLAYER_STATUS.idle,
      });
    }

    if (isHovered !== prevIsHovered) {
      this.loadVideo();
    }
  }

  componentWillUnmount() {
    this.clearSideEffect();
  }

  render() {
    const {
      userId,
      username,
      formattedViewers,
      isJumbo,
      disableHoverEffect,
      languageBadges,
      disableLink,
      disableOnMouseLeave,
      isLiveStreaming,
      isPrivateUserSubscribed,
      isKaraokeDeviceOnline,
      publicBadgeUrlPath,
    } = this.props;
    const shouldHydrate = isServer || !window.__IS_HYDRATED__;
    const renderCover = shouldHydrate
      ? this.renderHydrateCover
      : this.renderCover;

    return (
      <StyledLiveStreamCard
        onMouseEnter={disableHoverEffect ? undefined : this.handleOnMouseEnter}
        onMouseLeave={
          disableHoverEffect || disableOnMouseLeave
            ? undefined
            : this.handleOnMouseLeave
        }
        isJumbo={isJumbo}
      >
        {renderCover()}
        {this.renderPlayer()}
        {this.renderLocation()}
        <Overlay isJumbo={isJumbo}>
          <UserProfileLink
            to={`/user/${userId}/${LIVESTREAM_PATH}${
              isServer ? '' : location.search
            }`}
            onClick={this.handleClickCard}
            as={!userId || disableLink ? undefined : Link}
          />
        </Overlay>
        <UpperRightWrapper isJumbo={isJumbo}>
          {(isLiveStreaming ||
            (isLiveStreaming == null && !isPrivateUserSubscribed)) && (
            <LiveStreamBadge
              userId={userId}
              streamId={userId}
              type={LIVESTREAM_TYPE_STATUS_BADGE}
              size={18}
            />
          )}
          <div>
            {languageBadges.map(language => (
              <LanguageIcon
                key={language}
                src={getResourceUrl({
                  path: publicBadgeUrlPath,
                  endpoint: `/${language}.png`,
                  resourceType: PUBLIC,
                })}
                isJumbo={isJumbo}
              />
            ))}
            {isKaraokeDeviceOnline && (
              <LanguageIcon src={IconDeviceSource} isJumbo={isJumbo} />
            )}
          </div>
        </UpperRightWrapper>
        <InformationWrapper isJumbo={isJumbo}>
          <UsernameWrapper isJumbo={isJumbo}>
            <Username to={`/user/${userId}`} onClick={this.handleClickUsername}>
              {username}
            </Username>
          </UsernameWrapper>
          <Viewers isJumbo={isJumbo}>
            <ViewersIcon src={ViewersIconResource} aria-hidden />
            <ViewersAmountWrapper isJumbo={isJumbo}>
              <Flip>{formattedViewers}</Flip>
            </ViewersAmountWrapper>
          </Viewers>
        </InformationWrapper>
      </StyledLiveStreamCard>
    );
  }
}

LiveStreamCard.propTypes = {
  i18n: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  meId: PropTypes.string,
  userId: PropTypes.string,
  streamId: PropTypes.string,
  username: PropTypes.string,
  locationKey: PropTypes.string,
  formattedViewers: PropTypes.string,
  publicBadgeUrlPath: PropTypes.string,
  status: PropTypes.string,
  privateShowStatus: PropTypes.string,
  isJumbo: PropTypes.bool,
  disableHoverEffect: PropTypes.bool,
  disableOnMouseLeave: PropTypes.bool,
  disableLink: PropTypes.bool,
  isAuthed: PropTypes.bool,
  isIntersecting: PropTypes.bool,
  shouldAutoplay: PropTypes.bool,
  isLocationKeyTranslationFetched: PropTypes.bool,
  isLiveStreaming: PropTypes.bool,
  isPrivateUserSubscribed: PropTypes.bool,
  isKaraokeDeviceOnline: PropTypes.bool,
  languageBadges: PropTypes.array,
  fetchUser: PropTypes.func,
  subscribeOnlineStatus: PropTypes.func,
  fetchTranslationByKey: PropTypes.func,
};

LiveStreamCard.defaultProps = {
  meId: null,
  userId: null,
  streamId: null,
  username: null,
  formattedViewers: '-',
  locationKey: '',
  publicBadgeUrlPath: '',
  status: '',
  privateShowStatus: '',
  isAuthed: false,
  isJumbo: false,
  disableHoverEffect: false,
  disableOnMouseLeave: false,
  disableLink: false,
  isIntersecting: false,
  shouldAutoplay: false,
  isLocationKeyTranslationFetched: false,
  isLiveStreaming: null,
  isPrivateUserSubscribed: false,
  isKaraokeDeviceOnline: false,
  languageBadges: [],
  fetchUser: () => null,
  subscribeOnlineStatus: () => null,
  fetchTranslationByKey: () => null,
};

const StyledLiveStreamCard = styled.div`
  position: relative;
  border-radius: 4px;
  padding-top: 100%;
  overflow: hidden;
  display: block;
  :hover {
    opacity: 1;
  }
  @media (min-width: ${breakpoint.tablet}px) {
    ${({ isJumbo }) => {
      if (isJumbo) {
        return css`
          border-radius: 0px;
        `;
      }
    }}
  }
`;

const Overlay = styled.div`
  position: absolute;
  top: 0px;
  bottom: 0px;
  right: 0px;
  left: 0px;
  border-radius: 4px;
  background-image: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.5),
    rgba(0, 0, 0, 0.1) 20%,
    rgba(0, 0, 0, 0.04) 25%,
    rgba(0, 0, 0, 0.04) 33%,
    rgba(0, 0, 0, 0.5)
  );
  @media (min-width: ${breakpoint.tablet}px) {
    ${({ isJumbo }) => {
      if (isJumbo) {
        return css`
          border-radius: 0px;
        `;
      }
    }}
  }
`;

const UserProfileLink = styled.div`
  display: block;
  width: 100%;
  height: 100%;
  -webkit-touch-callout: none;
  user-select: none;
  user-drag: none;
`;

const UpperRightWrapper = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  @media (min-width: ${breakpoint.mobile}px) {
    top: ${({ isJumbo }) => isJumbo && '12px'};
    right: ${({ isJumbo }) => isJumbo && '12px'};
  }
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const InformationWrapper = styled.div`
  position: absolute;
  left: 8px;
  bottom: 8px;
  @media (min-width: ${breakpoint.mobile}px) {
    bottom: ${({ isJumbo }) => isJumbo && '12px'};
    left: ${({ isJumbo }) => isJumbo && '12px'};
  }
`;

const UsernameWrapper = styled.div`
  font-size: ${({ isJumbo }) => (isJumbo ? '18px' : '12px')};
  font-weight: 600;
  color: ${textColor.white};
`;

const Username = styled(Link)`
  ::before {
    content: '@';
  }
`;

const Viewers = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 2px;
  line-height: 1;
  opacity: 0.5;
  font-size: ${({ isJumbo }) => (isJumbo ? '12px' : '10px')};
  vertical-align: text-bottom;
`;

const ViewersIcon = styled.img`
  margin-right: 4px;
  width: 12px;
  height: 12px;
`;

const ViewersAmountWrapper = styled.div``;

const StatefulImageWrapper = styled.div`
  position: absolute;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
`;

const Picture = styled.picture`
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  opacity: ${loadingIconOpacity};
`;

const LoadingIcon = styled.img`
  width: 32px;
  height: auto;
`;

const PlayerWrapper = styled.div`
  position: absolute;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  video {
    position: absolute;
    top: 0px;
    bottom: 0px;
    left: 0px;
    right: 0px;
    object-fit: cover;
    transition: opacity 0.4s;
    ${({ shouldShowPlayer }) => `opacity: ${shouldShowPlayer ? 1 : 0};`}
    ${media.mobile`
      border-radius: 4px;
    `}
  }
`;

const LanguageIcon = styled.img.attrs(({ isJumbo }) => ({
  style: {
    width: isJumbo ? '20px' : '16px',
  },
}))`
  margin-top: 5px;
  margin-right: 4px;
  height: auto;
  :last-child {
    margin-right: 0px;
  }
  filter: drop-shadow(0 0 4px rgb(0 0 0 / 50%));
`;

const LocationWrapper = styled.div`
  max-width: 45%;
  position: absolute;
  display: flex;
  align-items: center;
  z-index: 1;
  bottom: 8px;
  right: 8px;
  @media (min-width: ${breakpoint.mobile}px) {
    bottom: ${({ isJumbo }) => isJumbo && '12px'};
    right: ${({ isJumbo }) => isJumbo && '12px'};
  }
`;

const LocationText = styled.div`
  font-size: ${({ isJumbo }) => (isJumbo ? '14px' : '12px')};
  line-height: 1;
  color: #fff;
  ${TextEllipsis}
`;

export default withTranslation()(LiveStreamCard);
